/**
 * @typedef {import('../types').FiltersStore} FiltersStore
 * @typedef {{ label: string, key: string, route?: string, category?: string|null, count?: number }} MarketEntry
 */

const CATEGORY_BUTTON_ATTR = 'data-props-more-category';
const MARKET_BUTTON_ATTR = 'data-props-more-market';
const QUICK_ROOT_SELECTOR = '[data-props-quick-chips]';

/**
 * Config-driven "All Props" panel with category filtering + search.
 * @param {FiltersStore} store
 */
export function registerMorePanelModule(store) {
  if (!store || typeof document === 'undefined') {
    return;
  }

  const trigger = document.querySelector('[data-props-more-trigger]');
  const panel = document.querySelector('[data-props-more-panel]');
  if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[filters] more panel missing trigger/panel', { trigger, panel });
    }
    return;
  }

  const quickRoot = document.querySelector(QUICK_ROOT_SELECTOR);

  /** @type {MarketEntry[]} */
  const quickMarkets = Array.isArray(store.config?.markets?.quick)
    ? store.config.markets.quick
    : [];
  /** @type {Set<string>} */
  const quickKeys = new Set();

  /** @type {{ name: string, markets: MarketEntry[] }[]} */
  const categoryGroups = Array.isArray(store.config?.markets?.categories)
    ? store.config.markets.categories
    : [];

  if (!quickMarkets.length && !categoryGroups.length) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[filters] more-panel config missing market definitions');
    }
  }

  const abbreviations =
    (store.config?.markets?.abbreviations &&
      typeof store.config.markets.abbreviations === 'object')
      ? store.config.markets.abbreviations
      : {};

  const routeMap =
    (store.config?.markets?.routeMap &&
      typeof store.config.markets.routeMap === 'object')
      ? store.config.markets.routeMap
      : {};

  const inlineEnabled = !!store.config?.featureFlags?.inlineMarketsEnabled;

  /** @type {Map<string, MarketEntry>} */
  const entryByKey = new Map();
  /** @type {Map<string, string>} */
  const categoryByKey = new Map();
  /** @type {Map<string, MarketEntry[]>} */
  const categoryMap = new Map();
  /** @type {MarketEntry[]} */
  let allEntries = [];

  let currentCategory = 'All';
  let searchTerm = '';

  const categoryContainer = panel.querySelector('.category-chips');
  const listContainer = panel.querySelector('.market-list[data-dynamic]');
  const searchInput = /** @type {HTMLInputElement|null} */ (
    panel.querySelector('#props-more-search')
  );
  // Normalize legacy markup so it behaves as an inline popover.
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  panel.setAttribute('role', 'listbox');
  panel.removeAttribute('aria-modal');
  panel.removeAttribute('data-dialog-state');
  panel.classList.remove('props-matchups-panel');
  panel.classList.remove('open');
  panel.style.display = '';
  panel.hidden = true;
  const moreFilter = trigger.closest('.props-more-filter') || null;
  const emptyState = document.createElement('div');
  emptyState.className = 'market-list-empty';
  emptyState.textContent = 'No matching props.';

  const moreCloseButton = panel.querySelector('[data-action="close-more"]');

  /** @type {Map<string, HTMLButtonElement>} */
  let listButtonsByKey = new Map();

  function slugify(label) {
    return String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function normaliseEntry(raw, fallbackCategory) {
    const label = raw?.label || '';
    const key = raw?.key || slugify(label);
    const route = raw?.route || routeMap[key] || '';
    const category =
      raw?.category !== undefined ? raw.category : fallbackCategory || null;

    return {
      label,
      key,
      route,
      category,
      count: typeof raw?.count === 'number' ? raw.count : undefined,
    };
  }

  function registerEntry(entry, categoryName) {
    if (!entry.label) {
      return;
    }

    entry.category =
      entry.category !== undefined ? entry.category : categoryName || null;

    if (!entryByKey.has(entry.key)) {
      entryByKey.set(entry.key, entry);
    }

    if (categoryName) {
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, []);
      }
      const list = categoryMap.get(categoryName);
      if (list && !list.some((item) => item.key === entry.key)) {
        list.push(entry);
      }
      categoryByKey.set(entry.key, categoryName);
    }
  }

  quickMarkets.forEach((raw) => {
    const entry = normaliseEntry(raw);
    registerEntry(entry, null);
    if (entry.key) {
      quickKeys.add(entry.key);
    }
  });

  categoryGroups.forEach((group) => {
    const name = group?.name || '';
    const markets = Array.isArray(group?.markets) ? group.markets : [];
    markets.forEach((raw) => {
      const entry = normaliseEntry(raw, name);
      registerEntry(entry, name);
    });
  });

  allEntries = Array.from(entryByKey.values());

  function getCountsPayload() {
    const source =
      quickRoot instanceof HTMLElement ? quickRoot : panel;
    const raw = source.getAttribute('data-market-counts') || '';
    if (!raw) {
      return {};
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[filters] failed to parse market counts for more panel', error);
      }
    }
    return {};
  }

  function formatCount(value) {
    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }
    return value > 999 ? '999+' : String(value);
  }

  function updateListCounts() {
    const counts = getCountsPayload();
    listButtonsByKey.forEach((button, key) => {
      const entry = entryByKey.get(key);
      if (!entry) {
        return;
      }
      const fallbackLabel = entry.label;
      const value =
        counts[key] !== undefined
          ? counts[key]
          : counts[fallbackLabel] !== undefined
            ? counts[fallbackLabel]
            : entry.count;
      const span =
        button.querySelector('.market-count') ||
        button.appendChild(document.createElement('span'));
      span.className = 'market-count';
      const formatted = formatCount(Number(value) || 0);
      if (formatted) {
        span.textContent = formatted;
        span.hidden = false;
      } else {
        span.textContent = '';
        span.hidden = true;
      }
    });
  }

  function navigateToMarket(entry) {
    const scope = panel.closest('.props-market-filter') || panel;
    const fallback = () => {
      if (entry.route) {
        window.location.assign(entry.route);
        return;
      }
      try {
        const url = new URL(window.location.href);
        if (entry.key) {
          url.searchParams.set('market', entry.key);
        } else {
          url.searchParams.delete('market');
        }
        window.location.assign(url.toString());
      } catch (error) {
        if (entry.key) {
          window.location.search = `?market=${encodeURIComponent(entry.key)}`;
        }
      }
    };

    if (
      inlineEnabled &&
      typeof window.propsInlineNavigate === 'function'
    ) {
      window.propsInlineNavigate(
        scope,
        'market',
        {
          marketKey: entry.key,
          marketLabel: entry.label,
          route: entry.route,
        },
        fallback
      );
    } else {
      fallback();
    }
  }

  function selectMarket(entry) {
    const button = listButtonsByKey.get(entry.key);
    if (button) {
      button.classList.add('loading');
    }
    if (store.actions && typeof store.actions.setActiveMarket === 'function') {
      store.actions.setActiveMarket(entry.key);
    } else {
      store.setState?.({ activeMarketKey: entry.key });
    }
    if (store.actions && typeof store.actions.setDialogOpen === 'function') {
      store.actions.setDialogOpen('more', false);
    }
    closePanel();
    navigateToMarket(entry);
  }

  function renderCategoryButtons() {
    if (categoryContainer instanceof HTMLElement) {
      categoryContainer.innerHTML = '';
    }
  }

  function updateCategorySelection() {}

  function deriveEntriesForCategory() {
    return allEntries;
  }

  function renderMarketList() {
    if (!(listContainer instanceof HTMLElement)) {
      return;
    }
    listButtonsByKey = new Map();
    listContainer.innerHTML = '';

    const counts = getCountsPayload();

    const activeKey = store.getState().activeMarketKey || '';
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let dataset = [];
    if (normalizedSearch) {
      dataset = allEntries.filter((entry) => {
        const label = entry.label.toLowerCase();
        const abbrev = abbreviations[entry.label]
          ? String(abbreviations[entry.label]).toLowerCase()
          : '';
        return (
          label.includes(normalizedSearch) ||
          abbrev.includes(normalizedSearch)
        );
      });
    } else {
      dataset = deriveEntriesForCategory(currentCategory);
    }

    if (!dataset.length) {
      listContainer.appendChild(emptyState);
      return;
    }

    dataset.forEach((entry) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'market-pill';
      button.setAttribute(MARKET_BUTTON_ATTR, entry.key);
      button.setAttribute('data-market', entry.label);
      button.setAttribute('data-market-key', entry.key);

      const labelSpan = document.createElement('span');
      labelSpan.className = 'market-label';
      labelSpan.textContent =
        abbreviations[entry.label] || entry.label;
      button.appendChild(labelSpan);

      const countSpan = document.createElement('span');
      countSpan.className = 'market-count';
      const value =
        counts[entry.key] !== undefined
          ? counts[entry.key]
          : counts[entry.label] !== undefined
            ? counts[entry.label]
            : entry.count;
      const formatted = formatCount(Number(value) || 0);
      if (formatted) {
        countSpan.textContent = formatted;
        countSpan.hidden = false;
      } else {
        countSpan.textContent = '';
        countSpan.hidden = true;
      }
      button.appendChild(countSpan);

      const isActive = activeKey === entry.key;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      button.addEventListener('click', (event) => {
        event.preventDefault();
        selectMarket(entry);
      });

      listContainer.appendChild(button);
      listButtonsByKey.set(entry.key, button);
    });
  }

  function updateActiveState(state) {
    const { activeMarketKey } = state;
    listButtonsByKey.forEach((button, key) => {
      const isActive = key === activeMarketKey;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (!isActive) {
        button.classList.remove('loading');
      }
    });

    const quickSelected = quickKeys.has(activeMarketKey);

    trigger.setAttribute('aria-pressed', quickSelected ? 'false' : 'true');
    trigger.classList.toggle('active', !quickSelected);
  }

  if (categoryContainer instanceof HTMLElement) {
    categoryContainer.innerHTML = '';
  }

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      searchTerm = searchInput.value || '';
      if (searchTerm) {
        currentCategory = 'All';
        updateCategorySelection();
      }
      renderMarketList();
    });
  }

  if (moreCloseButton instanceof HTMLElement) {
    moreCloseButton.addEventListener('click', (event) => {
      event.preventDefault();
      closePanel();
    });
  }

  renderMarketList();
  updateListCounts();

  const unsubscribe = store.subscribe((state) => {
    updateActiveState(state);
  });

  const observerTarget =
    quickRoot instanceof HTMLElement ? quickRoot : panel;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-market-counts'
      ) {
        updateListCounts();
      }
    }
  });

  observer.observe(observerTarget, {
    attributes: true,
    attributeFilter: ['data-market-counts'],
  });

  let isOpen = false;

  function openPanel() {
    if (isOpen) return;
    isOpen = true;
    if (store.actions && typeof store.actions.setDialogOpen === 'function') {
      store.actions.setDialogOpen('more', true);
    }
    searchTerm = '';
    currentCategory = 'All';
    updateCategorySelection();
    renderMarketList();
    updateActiveState(store.getState());
    updateListCounts();
    trigger.setAttribute('aria-expanded', 'true');
    if (moreFilter) {
      moreFilter.classList.add('open');
    }
    panel.style.display = 'grid';
    panel.removeAttribute('hidden');
  }

  function closePanel() {
    if (!isOpen) return;
    isOpen = false;
    if (store.actions && typeof store.actions.setDialogOpen === 'function') {
      store.actions.setDialogOpen('more', false);
    }
    currentCategory = 'All';
    searchTerm = '';
    trigger.setAttribute('aria-expanded', 'false');
    if (moreFilter) {
      moreFilter.classList.remove('open');
    }
    panel.style.display = '';
    panel.setAttribute('hidden', 'true');
    if (searchInput) {
      searchInput.value = '';
    }
    updateCategorySelection();
    renderMarketList();
  }


  panel.addEventListener(
    'filters:teardown',
    () => {
      observer.disconnect();
      unsubscribe();
      listButtonsByKey.clear();
      document.removeEventListener('pointerdown', handleOutsideClick, true);
      document.removeEventListener('keydown', handleEscape, true);
    },
    { once: true }
  );

  function handleOutsideClick(event) {
    if (!isOpen) {
      return;
    }
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (panel.contains(target) || trigger.contains(target)) {
      return;
    }
    closePanel();
  }

  function handleEscape(event) {
    if (event.key !== 'Escape') {
      return;
    }
    closePanel();
    trigger.focus({ preventScroll: true });
  }

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  });

  document.addEventListener('pointerdown', handleOutsideClick, true);
  document.addEventListener('keydown', handleEscape, true);

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[filters] more panel initialised', {
      categories: categoryGroups.map((group) => group.name),
      totalMarkets: allEntries.length,
    });
  }
}
